import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Trash2, Unlink, Link2 } from 'lucide-react';
import {
  PropertyTextField,
  PropertyNumberField,
  PropertySelectField,
  PropertyColorField,
} from './PropertyFields';
import { ModifyObjectCommand } from '../../../commands/ModifyObjectCommand';
import { getObjectTypeById } from '../../../config';
import {
  resolveParentById,
  syncLinkedGates,
  getParentHeightCm,
  getGardenTypeId,
  WINDOW_TYPE_ID,
} from '../tools/gateLinkUtils';
import ConfirmDialog from '../../ui/ConfirmDialog';

/**
 * GateProperties — dedicated properties panel for gates ('tor', #611, Block C)
 *
 * Gates carry a richer, non-config-driven model than the generic dynamic
 * panel handles: a link status to the host structure, opening-width presets,
 * an auto/custom height mode that inherits the structure height, and the two
 * symbol parameters (hinge, swing). Legacy standalone gates (without the new
 * hinge field) still use DynamicGardenObjectProperties — MainLayout dispatches
 * here only for symbol gates.
 *
 * All edits mutate the Fabric object immediately (live symbol re-render via
 * onUpdate) and are coalesced into a single debounced ModifyObjectCommand for
 * undo/redo, mirroring the DynamicGardenObjectProperties pattern.
 */

export const WIDTH_PRESETS = [80, 100, 120];
const WIDTH_MIN = 60;
const WIDTH_MAX = 500;
const HEIGHT_MIN = 80;
const HEIGHT_MAX = 250;
// Window-specific bounds (#690) — mirror the FENSTER config; kept as module
// constants (like HEIGHT_MIN/MAX) so the onChange handlers don't reference
// render-local values (react-hooks/immutability).
const WINDOW_HEIGHT_MIN = 40;
const WINDOW_HEIGHT_MAX = 250;
const SILL_MIN = 0;
const SILL_MAX = 200;
const SILL_DEFAULT = 90;
const COMMAND_DEBOUNCE_MS = 400;

const HINGE_OPTIONS = [
  { value: 'left', label: 'Links' },
  { value: 'right', label: 'Rechts' },
  { value: 'double', label: 'Doppelflügel' },
];
const SWING_OPTIONS = [
  { value: 'in', label: 'Innen' },
  { value: 'out', label: 'Außen' },
];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const GateProperties = ({ gate, canvas, commandStack, onUpdate, onDelete }) => {
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick((n) => n + 1), []);
  const oldPropsRef = useRef(null);
  const debounceRef = useRef(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Windows (#690) share this panel but drop hinge/swing and add a sill height.
  const typeId = getGardenTypeId(gate);
  const isWindow = typeId === WINDOW_TYPE_ID;
  const panelTitle = isWindow ? 'Fenster' : 'Tür / Tor';
  const materialOptions = getObjectTypeById(typeId)?.properties?.mvp?.material?.options || [];

  const data = gate.data || {};
  const link = data.link || null;
  const linked = !!link;
  const widthCm = Math.round((gate.width || 0) * (gate.scaleX || 1));
  const heightMode = data.heightMode || (linked ? 'auto' : 'custom');
  const heightCm = Math.round(data.height ?? 120);
  const sillCm = Math.round(data.sillHeight ?? SILL_DEFAULT);

  // Resolve the host structure's display name for the link status line
  let parentName = null;
  if (linked && canvas) {
    const parent = resolveParentById(canvas, link.parentId);
    if (parent) {
      parentName =
        parent.data?.name?.trim() ||
        getObjectTypeById(parent.data?.objectTypeId || parent.data?.gardenObjectType)?.name ||
        'Struktur';
    }
  }

  // --- undo/redo: capture once per burst, flush debounced ------------------
  const snapshot = useCallback(
    () => ({
      data: { ...gate.data },
      width: gate.width,
      scaleX: gate.scaleX,
      left: gate.left,
      top: gate.top,
      angle: gate.angle,
    }),
    [gate]
  );

  const captureOld = useCallback(() => {
    if (!oldPropsRef.current) oldPropsRef.current = snapshot();
  }, [snapshot]);

  const flushCommand = useCallback(() => {
    if (oldPropsRef.current && commandStack) {
      commandStack.execute(new ModifyObjectCommand(gate, oldPropsRef.current, snapshot()));
      oldPropsRef.current = null;
      if (gate.canvas) gate.canvas.fire('object:modified', { target: gate });
    }
  }, [commandStack, gate, snapshot]);

  const scheduleCommand = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      flushCommand();
      debounceRef.current = null;
    }, COMMAND_DEBOUNCE_MS);
  }, [flushCommand]);

  // Flush any pending command when the gate changes or the panel unmounts
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        flushCommand();
      }
    },
    [flushCommand]
  );

  // Re-render the panel when this gate or its host structure moves/changes, so
  // link status, position-derived width and the inherited height stay live.
  useEffect(() => {
    if (!canvas) return undefined;
    const handler = (e) => {
      const t = e?.target;
      if (t === gate || (link && t?.data?.objectId === link.parentId)) {
        rerender();
      }
    };
    canvas.on('object:moving', handler);
    canvas.on('object:modified', handler);
    return () => {
      canvas.off('object:moving', handler);
      canvas.off('object:modified', handler);
    };
  }, [canvas, gate, link, rerender]);

  // --- mutations -----------------------------------------------------------
  const applyDataPatch = useCallback(
    (patch) => {
      captureOld();
      // eslint-disable-next-line react-hooks/immutability -- imperative Fabric.js mutation; .set() doesn't deep-merge nested data
      gate.data = { ...gate.data, ...patch };
      gate.setCoords?.();
      onUpdate?.();
      rerender();
      scheduleCommand();
    },
    [captureOld, gate, onUpdate, rerender, scheduleCommand]
  );

  const applyWidth = useCallback(
    (cm) => {
      const clamped = clamp(cm, WIDTH_MIN, WIDTH_MAX);
      captureOld();
      gate.set({ width: clamped, scaleX: 1 });
      // Keep it seated on its edge and clamp the position so it can't overhang
      if (link && canvas) {
        const parent = resolveParentById(canvas, link.parentId);
        if (parent) syncLinkedGates(canvas, parent);
      }
      gate.setCoords();
      onUpdate?.();
      rerender();
      scheduleCommand();
    },
    [captureOld, gate, link, canvas, onUpdate, rerender, scheduleCommand]
  );

  const handleHeightMode = useCallback(
    (mode) => {
      if (mode === 'auto' && link && canvas) {
        const parent = resolveParentById(canvas, link.parentId);
        const parentHeight = getParentHeightCm(parent);
        applyDataPatch({
          heightMode: 'auto',
          ...(parentHeight !== null
            ? { height: clamp(parentHeight, HEIGHT_MIN, HEIGHT_MAX) }
            : {}),
        });
      } else {
        applyDataPatch({ heightMode: 'custom' });
      }
    },
    [applyDataPatch, link, canvas]
  );

  const handleDetach = useCallback(() => {
    captureOld();
    const next = { ...gate.data };
    delete next.link;
    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric.js mutation; detaching the gate link
    gate.data = next;
    onUpdate?.();
    rerender();
    scheduleCommand();
  }, [captureOld, gate, onUpdate, rerender, scheduleCommand]);

  const heightAuto = heightMode === 'auto' && linked;

  return (
    <div className="flex flex-col bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Eigenschaften - {panelTitle}</h3>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Name */}
        <PropertyTextField
          label="Name"
          value={data.name || ''}
          onChange={(v) => applyDataPatch({ name: v })}
          placeholder=""
        />

        {/* Link status */}
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2.5">
          {linked ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Link2 size={15} className="text-emerald-600 shrink-0" />
                <span>
                  Verbunden mit: <span className="font-medium">{parentName}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={handleDetach}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium
                  text-gray-700 bg-white border border-gray-300 rounded
                  hover:bg-gray-100 hover:border-gray-400 transition-colors"
              >
                <Unlink size={13} />
                Lösen
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Unlink size={15} className="shrink-0" />
              <span>Freistehend</span>
            </div>
          )}
        </div>

        {/* Opening width with presets */}
        <div>
          <span className="block text-xs font-medium text-gray-600 mb-1">Breite</span>
          <div className="flex gap-1.5 mb-2">
            {WIDTH_PRESETS.map((preset) => {
              const active = widthCm === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => applyWidth(preset)}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors ${
                    active
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {preset} cm
                </button>
              );
            })}
          </div>
          <PropertyNumberField
            label="Frei"
            value={widthCm}
            onChange={applyWidth}
            unit="cm"
            min={WIDTH_MIN}
            max={WIDTH_MAX}
            step={5}
            commitOnBlur
          />
        </div>

        {isWindow ? (
          <>
            {/* Height (windows keep their own height, no parent inheritance) */}
            <PropertyNumberField
              label="Höhe"
              value={heightCm}
              onChange={(v) => applyDataPatch({ height: clamp(v, WINDOW_HEIGHT_MIN, WINDOW_HEIGHT_MAX) })}
              unit="cm"
              min={WINDOW_HEIGHT_MIN}
              max={WINDOW_HEIGHT_MAX}
              step={5}
              commitOnBlur
            />

            {/* Sill height: lower edge above the floor (#690). 3D clamps
                sillHeight + height to the parent wall height. */}
            <PropertyNumberField
              label="Brüstungshöhe"
              value={sillCm}
              onChange={(v) => applyDataPatch({ sillHeight: clamp(v, SILL_MIN, SILL_MAX) })}
              unit="cm"
              min={SILL_MIN}
              max={SILL_MAX}
              step={5}
              commitOnBlur
            />
          </>
        ) : (
          <>
            {/* Height mode */}
            <div>
              <span className="block text-xs font-medium text-gray-600 mb-1">Höhe</span>
              <div className="flex gap-1.5 mb-2">
                <button
                  type="button"
                  onClick={() => handleHeightMode('auto')}
                  disabled={!linked}
                  title={linked ? '' : 'Nur für mit einer Struktur verbundene Tore'}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed ${
                      heightAuto
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                >
                  Automatisch
                </button>
                <button
                  type="button"
                  onClick={() => handleHeightMode('custom')}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors ${
                    !heightAuto
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Benutzerdefiniert
                </button>
              </div>
              <PropertyNumberField
                label={heightAuto ? 'Erbt Strukturhöhe' : 'Höhe'}
                value={heightCm}
                onChange={(v) => applyDataPatch({ height: clamp(v, HEIGHT_MIN, HEIGHT_MAX), heightMode: 'custom' })}
                unit="cm"
                min={HEIGHT_MIN}
                max={HEIGHT_MAX}
                step={5}
                disabled={heightAuto}
                commitOnBlur
              />
            </div>

            {/* Hinge (Aufhängung/Anschlag) */}
            <PropertySelectField
              label="Aufhängung"
              value={data.hinge || 'left'}
              onChange={(v) => applyDataPatch({ hinge: v })}
              options={HINGE_OPTIONS}
            />

            {/* Swing (Öffnungsrichtung) */}
            <PropertySelectField
              label="Öffnungsrichtung"
              value={data.swing || 'in'}
              onChange={(v) => applyDataPatch({ swing: v })}
              options={SWING_OPTIONS}
            />
          </>
        )}

        {/* Material (metadata for 3D; no 2D texture) */}
        {materialOptions.length > 0 && (
          <PropertySelectField
            label="Material"
            value={data.material || materialOptions[0].value}
            onChange={(v) => applyDataPatch({ material: v })}
            options={materialOptions}
          />
        )}

        {/* Color drives the symbol line color */}
        <PropertyColorField
          label={isWindow ? 'Rahmenfarbe' : 'Farbe'}
          value={data.color || (isWindow ? '#5B7C8A' : '#2F4F4F')}
          onChange={(v) => applyDataPatch({ color: v })}
        />
      </div>

      {/* Delete */}
      <div className="px-4 py-3 border-t border-gray-200">
        <button
          type="button"
          onClick={() => setShowDeleteDialog(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2
            bg-red-50 border border-red-300 text-red-700 rounded
            hover:bg-red-100 hover:border-red-400 transition-all font-medium text-sm"
        >
          <Trash2 size={16} />
          Löschen
        </button>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDelete?.(gate);
        }}
        onCancel={() => setShowDeleteDialog(false)}
        title={isWindow ? 'Fenster löschen' : 'Tür / Tor löschen'}
        message={
          isWindow
            ? 'Möchten Sie dieses Fenster wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchten Sie diese Tür / dieses Tor wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

GateProperties.propTypes = {
  gate: PropTypes.object.isRequired,
  canvas: PropTypes.object,
  commandStack: PropTypes.object,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

export default GateProperties;
